<template>
  <div class="article-attr">
    <div class="article-attr__tips">
      <span v-if="selectedCategoryName">当前分类：{{ selectedCategoryName }}（ID: {{ selectedCategoryId }}）</span>
      <span v-else-if="selectedCategoryId">当前分类ID：{{ selectedCategoryId }}</span>
      <span v-else>请先选择文章分类</span>
    </div>

    <CompDragList
      v-model="articleList"
      type="text"
      btn-text="选择文章分类"
      :show-title="true"
      title="文章"
      :is-move="false"
      :is-delete="false"
      @onAddItem="handleAddItem"
    >
      <template slot="body" slot-scope="scope">
        <div class="article-attr-item">
          <el-image
            v-if="scope.data.image_url"
            :src="scope.data.image_url"
            fit="cover"
            class="article-attr-item__img"
          />
          <div v-else class="article-attr-item__placeholder">无封面</div>
          <div class="article-attr-item__content">
            <div class="article-attr-item__title">{{ scope.data.title || '未命名文章' }}</div>
            <div class="article-attr-item__id">文章ID：{{ scope.data.id || '--' }}</div>
          </div>
        </div>
      </template>
    </CompDragList>
  </div>
</template>

<script>
import { getArticleManagementList, getArticleCategoryInfo } from '@/api/article'
import CompDragList from '../../comps/comp-dragList'

function parseMaybeJSON(val) {
  if (!val) return null
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return null
    }
  }
  return val
}

function extractListPayload(response) {
  const payload = (response && response.data && response.data.data) || (response && response.data) || {}
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.list)) return payload.list
  if (Array.isArray(payload.rows)) return payload.rows
  return []
}

function normalizeCategoryId(val) {
  if (val == null || val === '') return ''
  if (Array.isArray(val)) {
    const first = val[0] || {}
    return first.category_id || first.id || ''
  }
  if (typeof val === 'object') {
    return val.category_id || val.id || ''
  }
  return val
}

function normalizeArticleList(list) {
  if (!Array.isArray(list)) return []
  return list.map((item) => ({
    id: item.article_id || item.id,
    title: item.title || item.article_title || '',
    image_url: item.image_url || item.url || item.cover_img || '',
    article_id: item.article_id || item.id,
    article_type: item.article_type || ''
  }))
}

export default {
  name: 'ArticleAttrItem',
  components: { CompDragList },
  props: {
    uuid: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number, Object, Array],
      default: ''
    }
  },
  data() {
    return {
      selectedCategoryId: '',
      selectedCategoryName: '',
      articleList: []
    }
  },
  watch: {
    value: {
      async handler(val) {
        const nextCategoryId = normalizeCategoryId(val)
        const embeddedArticles = normalizeArticleList(val && typeof val === 'object' ? (val.articles || val.list) : [])
        const sameCategory = String(nextCategoryId) === String(this.selectedCategoryId || '')

        if (sameCategory && embeddedArticles.length) {
          this.articleList = embeddedArticles
          return
        }

        if (sameCategory && this.articleList.length) return

        this.selectedCategoryId = nextCategoryId
        this.selectedCategoryName = ''

        if (embeddedArticles.length) {
          this.articleList = embeddedArticles
          return
        }
        if (!nextCategoryId) {
          this.articleList = []
          return
        }
        await this.fetchCategoryName(nextCategoryId)
        await this.fetchArticlesByCategory(nextCategoryId)
      },
      immediate: true
    },
    uuid: {
      handler() {
        const currentId = normalizeCategoryId(this.value)
        if (String(currentId) !== String(this.selectedCategoryId || '')) {
          this.selectedCategoryId = currentId
        }
      },
      immediate: true
    }
  },
  methods: {
    syncCategoryValue(articles = []) {
      if (!this.selectedCategoryId) return
      this.$emit('input', {
        category_id: this.selectedCategoryId,
        category_name: this.selectedCategoryName || '',
        articles: Array.isArray(articles) ? articles : []
      })
    },
    async handleAddItem() {
      try {
        const { data } = await this.$picker.articleCategory({
          data: [],
          multiple: false
        })
        const selected = Array.isArray(data) ? data : []
        if (!selected.length) return

        const category = selected[0]
        const categoryId = normalizeCategoryId(category)
        if (!categoryId) {
          this.$message.warning('未获取到分类ID')
          return
        }

        this.selectedCategoryId = categoryId
        this.selectedCategoryName = category.category_name || category.name || ''
        const mapped = await this.fetchArticlesByCategory(categoryId)
        this.syncCategoryValue(mapped)
      } catch (e) {
        // 用户取消选择时忽略
      }
    },
    async fetchCategoryName(categoryId) {
      try {
        const response = await getArticleCategoryInfo(categoryId)
        const payload = (response && response.data && response.data.data) || (response && response.data) || {}
        this.selectedCategoryName = payload.category_name || payload.name || ''
      } catch (e) {
        this.selectedCategoryName = ''
      }
    },
    async fetchArticlesByCategory(categoryId) {
      const response = await getArticleManagementList({
        category_id: categoryId,
        page: 1,
        pageSize: 200
      })
      const list = extractListPayload(response)
      const mapped = list.map((item) => {
        const articleId = item.article_id || item.id
        return {
          id: articleId,
          title: item.title || '',
          image_url: this.resolveArticleCover(item),
          article_id: articleId,
          article_type: item.article_type || ''
        }
      })
      this.articleList = mapped
      if (!mapped.length) {
        this.$message.warning('当前分类下暂无文章')
      }
      this.syncCategoryValue(mapped)
      return mapped
    },
    resolveArticleCover(article) {
      if (!article || typeof article !== 'object') return ''
      if (article.image_url) return article.image_url
      if (article.cover_img) return article.cover_img
      if (article.cover) return article.cover
      if (article.url) return article.url

      const content = parseMaybeJSON(article.content) || parseMaybeJSON(article.materials)
      if (!Array.isArray(content) || !content.length) return ''
      const first = content[0] || {}
      return first.material_url || first.url || first.cover_img || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.article-attr__tips {
  margin-bottom: 8px;
  color: #909399;
}

.article-attr-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .article-attr-item__img,
  .article-attr-item__placeholder {
    width: 86px;
    height: 86px;
    border-radius: 6px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .article-attr-item__placeholder {
    border: 1px dashed #dcdfe6;
    color: #c0c4cc;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
  }

  .article-attr-item__content {
    flex: 1;
    min-width: 0;
  }

  .article-attr-item__title {
    font-size: 14px;
    color: #303133;
    line-height: 20px;
    margin-bottom: 4px;
    word-break: break-all;
  }

  .article-attr-item__id {
    font-size: 12px;
    color: #909399;
  }
}
</style>

<style lang="scss">
.article-attr {
  .drag-list {
    border: 1px solid #e0e0e0;

    .item-hd {
      padding: 5px 10px;
      border-bottom: 1px solid #e0e0e0;
    }

    .item-bd {
      padding: 10px 10px 0;
    }
  }
}
</style>
